import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { clearAuthToken } from '../api/config';
import { navigateTo } from '../utils/navigation';
import { resendVerificationEmail } from '../api/endpoints';
import { getErrorMessage } from '../utils/errors';

export default function VerifyEmailPendingPage(): React.JSX.Element {
  const params = new URLSearchParams(useLocation().search);
  const initialEmail = params.get('email') ?? '';
  const [email, setEmail] = useState(initialEmail);
  const readOnly = !!initialEmail;

  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => { setCooldown((s) => s - 1); }, 1000);
    return () => { clearInterval(id); };
  }, [cooldown]);

  const [copied, setCopied] = useState(false);
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 1200);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleResend = async () => {
    if (!email || sending) return;
    setSending(true);
    setStatus(null);
    try {
      await resendVerificationEmail(email);
      setStatus({ type: 'success', message: 'Verification email sent.' });
      setCooldown(30);
    } catch (error_: unknown) {
      setStatus({ type: 'error', message: getErrorMessage(error_, 'Failed to send verification email.') });
    } finally {
      setSending(false);
    }
  };

  const tips = [
    'It can take up to a minute to arrive.',
    'Check Spam/Junk and Promotions folders.',
  ];

  return (
    <div className="auth-page">
      <div className="orb" aria-hidden style={{ position: 'absolute', bottom: -80, right: -80, opacity: 0.4 }} />
      <div style={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 1 }}>
        <div className="card stack-5">
          <div className="stack-2">
            <h1 className="t-h3" style={{ margin: 0 }}>Verify your <em className="t-italic">email</em></h1>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              We sent a verification link to the address below. Open it to activate your account.
            </p>
          </div>

          <div className="stack-2">
            <label htmlFor="verify-email-input" className="t-label">Email</label>
            <div className="row-3">
              <input
                id="verify-email-input"
                type="email"
                value={email}
                readOnly={readOnly}
                onChange={(e) => { setEmail(e.target.value); }}
                aria-readonly={readOnly ? 'true' : 'false'}
                tabIndex={readOnly ? -1 : 0}
                className={['form-input', readOnly ? 'form-input--readonly' : ''].filter(Boolean).join(' ')}
                style={{ flex: 1 }}
              />
              <button type="button" onClick={copyEmail} className="link t-small" style={{ flexShrink: 0 }}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          <div className="card card--sand card--compact card--flat card--bordered">
            <div className="stack-2">
              {tips.map((tip, i) => (
                <div key={i} className="row-3" style={{ alignItems: 'flex-start' }}>
                  <span className="list-dot" />
                  <span className="t-small">{tip}</span>
                </div>
              ))}
              <div className="row-3" style={{ alignItems: 'flex-start' }}>
                <span className="list-dot" />
                <span className="t-small">
                  Add{' '}
                  <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 'inherit' }}>
                    no-reply@kivo.skin
                  </code>{' '}
                  to contacts.
                </span>
              </div>
            </div>
          </div>

          {status && (
            <div className={`notice notice--${status.type === 'success' ? 'success' : 'error'}`}>
              {status.message}
            </div>
          )}

          <div className="row-3" style={{ flexWrap: 'wrap' }}>
            <button
              className="btn btn--primary"
              onClick={handleResend}
              disabled={cooldown > 0 || sending}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : sending ? 'Sending…' : 'Resend verification email'}
            </button>
            <button
              className="btn btn--ghost"
              onClick={() => {
                clearAuthToken();
                navigateTo('/login');
              }}
            >
              Log out
            </button>
          </div>

          <p className="t-small t-mute" style={{ margin: 0, textAlign: 'center' }}>
            Wrong email?{' '}
            <button onClick={() => { navigateTo('/register'); }} className="link">
              Use another address
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
